/**
 * UAE delivery-promise windows (Asia/Dubai, UTC+4, no DST). Pure module, no
 * React — mirrors getIstParts in ../DeliveryEtaBadge/useSddNddEligibility.ts
 * but is purely frontend: no pincode, no serviceability API, applies to all
 * of the UAE.
 *
 *   00:00–09:59 GST → same_day   (arrives later today)
 *   10:00–17:59 GST → four_hour  (order now, arrives within 4 hours)
 *   18:00–23:59 GST → next_day   (order now, arrives tomorrow)
 */
export type AeDeliveryWindow = 'same_day' | 'four_hour' | 'next_day';

/** Minutes-of-day boundaries, Asia/Dubai. 10:00 inclusive starts FOUR_HOUR;
 * 18:00 inclusive starts NEXT_DAY. */
export const SAME_DAY_END = 10 * 60;
export const FOUR_HOUR_END = 18 * 60;

type DubaiParts = { hour: number; minute: number; second: number };

// Module-level singleton — constructing Intl.DateTimeFormat is comparatively
// expensive and this runs on every render tick / timer sync.
const dubaiTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Dubai',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const getDubaiParts = (date: Date): DubaiParts => {
  const parts = dubaiTimeFormatter.formatToParts(date);
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? '0';
  return {
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
};

export const resolveAeDeliveryWindow = (
  now: Date = new Date(),
): AeDeliveryWindow => {
  const { hour, minute } = getDubaiParts(now);
  const minutesOfDay = hour * 60 + minute;
  if (minutesOfDay < SAME_DAY_END) return 'same_day';
  if (minutesOfDay < FOUR_HOUR_END) return 'four_hour';
  return 'next_day';
};

/** Milliseconds until the next boundary (10:00, 18:00, or midnight GST), so
 * a mounted component can re-render exactly when the window flips instead
 * of polling. +50ms guards against firing a tick early from timer jitter. */
export const msUntilNextAeBoundary = (now: Date = new Date()): number => {
  const { hour, minute, second } = getDubaiParts(now);
  const secondsOfDay = hour * 3600 + minute * 60 + second;
  const boundarySeconds = [SAME_DAY_END * 60, FOUR_HOUR_END * 60, 24 * 3600];
  const nextBoundarySeconds =
    boundarySeconds.find((boundary) => boundary > secondsOfDay) ?? 24 * 3600;
  const msUntil =
    (nextBoundarySeconds - secondsOfDay) * 1000 - now.getMilliseconds() + 50;
  return Math.max(msUntil, 0);
};
